import { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { getProductReviews, submitProductReview } from "../services/orderPortal";
import { fetchReviewsFromSheet } from "../utils/sheetReviews";
import { getCustomerSession } from "../utils/customerSession";
import StarsDisplay from "./StarsDisplay";
import StarRatingInput from "./StarRatingInput";
import { BtnContent } from "./BtnIcon";
import { IoSendOutline } from "react-icons/io5";

function formatReviewDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function ProductReviews({ productId }) {
  const location = useLocation();
  const sectionRef = useRef(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const session = getCustomerSession();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [orderId, setOrderId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState(null);
  const [submitErr, setSubmitErr] = useState(null);

  useEffect(() => {
    if (location.state?.prefillOrderId) {
      setOrderId(String(location.state.prefillOrderId));
    }
  }, [location.state]);

  useEffect(() => {
    if (location.state?.openReview && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.state, productId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!productId) {
        setLoading(false);
        setReviews([]);
        return;
      }
      setLoading(true);
      setError(null);
      const res = await getProductReviews(productId);
      if (cancelled) return;
      let list = [];
      if (res.ok) {
        list = Array.isArray(res.data?.reviews) ? res.data.reviews : [];
      }
      if (!list.length) {
        try {
          list = await fetchReviewsFromSheet({ productId, limit: 50 });
        } catch {
          /* opensheet fallback unavailable */
        }
      }
      setLoading(false);
      if (!res.ok && !list.length) {
        setError(res.message || "Could not load reviews.");
        setReviews([]);
        return;
      }
      setReviews(list);
    })();
    return () => {
      cancelled = true;
    };
  }, [productId]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitMsg(null);
    setSubmitErr(null);
    if (!session?.token || !session.email) {
      setSubmitErr("Log in to submit a review.");
      return;
    }
    if (rating < 1 || rating > 5) {
      setSubmitErr("Choose a star rating from 1 to 5.");
      return;
    }
    if (comment.trim().length < 10) {
      setSubmitErr("Please write at least 10 characters.");
      return;
    }
    setSubmitting(true);
    const res = await submitProductReview({
      email: session.email,
      sessionToken: session.token,
      productId,
      orderId: orderId.trim(),
      rating,
      comment: comment.trim(),
    });
    setSubmitting(false);
    if (!res.ok) {
      setSubmitErr(res.message || "Could not save your review.");
      return;
    }
    setSubmitMsg(res.data?.message || "Thank you! Your review was submitted.");
    setRating(0);
    setComment("");
    setOrderId(location.state?.prefillOrderId ? String(location.state.prefillOrderId) : "");
  };

  const avg =
    reviews.length > 0
      ? reviews.reduce((s, r) => s + (Number(r.rating) || 0), 0) / reviews.length
      : 0;

  const loginState = {
    fromPath: location.pathname,
    reviewOpen: Boolean(location.state?.openReview),
    prefillOrderId: location.state?.prefillOrderId ? String(location.state.prefillOrderId) : "",
  };

  return (
    <section
      ref={sectionRef}
      id="product-reviews"
      className="product-reviews card border-0 shadow-sm mt-4"
      aria-labelledby="product-reviews-heading"
    >
      <div className="card-body">
        <h2 id="product-reviews-heading" className="h5 mb-3">
          Customer reviews
        </h2>

        {loading && <p className="small text-muted mb-0">Loading reviews…</p>}
        {error && <p className="small text-danger mb-2">{error}</p>}

        {!loading && !error && reviews.length > 0 && (
          <div className="mb-4">
            <p className="small text-muted mb-2">
              <strong>{reviews.length}</strong> verified review{reviews.length !== 1 ? "s" : ""}
              {avg > 0 && (
                <>
                  {" "}
                  · average <StarsDisplay value={avg} /> {avg.toFixed(1)}
                </>
              )}
            </p>
            <ul className="list-unstyled mb-0">
              {reviews.map((r, idx) => (
                <li key={`${r.timestamp}-${idx}`} className="border-bottom border-light py-3">
                  <div className="d-flex flex-wrap justify-content-between gap-2 align-items-start">
                    <StarsDisplay value={r.rating} />
                    <span className="small text-muted">{formatReviewDate(r.timestamp)}</span>
                  </div>
                  <p className="small mb-1 mt-2">{r.comment}</p>
                  <p className="small text-muted mb-0">— {r.reviewer || "Customer"}</p>
                </li>
              ))}
            </ul>
          </div>
        )}

        {!loading && !error && reviews.length === 0 && (
          <p className="small text-muted mb-4">No published reviews for this gift yet. Be the first after your purchase.</p>
        )}

        <h3 className="h6 mb-2">Write a review</h3>
        {session ? (
          <form onSubmit={onSubmit} className="small">
            <div className="mb-2">
              <label className="form-label mb-1">Your rating</label>
              <StarRatingInput value={rating} onChange={setRating} disabled={submitting} />
            </div>
            <div className="mb-2">
              <label className="form-label mb-1" htmlFor="review-order-id">
                Order ID <span className="text-muted fw-normal">(optional, helps us verify)</span>
              </label>
              <input
                id="review-order-id"
                type="text"
                className="form-control form-control-sm"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="e.g. from your order confirmation email"
                disabled={submitting}
                autoComplete="off"
              />
            </div>
            <div className="mb-2">
              <label className="form-label mb-1" htmlFor="review-comment">
                Comment
              </label>
              <textarea
                id="review-comment"
                className="form-control form-control-sm"
                rows={4}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Tell others about the box quality, finish, and gifting experience (min. 10 characters)."
                disabled={submitting}
                maxLength={2000}
              />
            </div>
            {submitErr && <p className="text-danger small mb-2">{submitErr}</p>}
            {submitMsg && <p className="text-success small mb-2">{submitMsg}</p>}
            <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
              <BtnContent icon={IoSendOutline}>
                {submitting ? "Submitting…" : "Submit review"}
              </BtnContent>
            </button>
            <p className="text-muted mt-2 mb-0" style={{ fontSize: "0.75rem" }}>
              One review per product per account. To hide a review later, set its Status to <strong>pending</strong> in
              the Reviews sheet.
            </p>
          </form>
        ) : (
          <p className="small text-muted mb-0">
            <Link to="/login" state={{ from: loginState }}>
              Log in
            </Link>{" "}
            with the email you used at checkout to post a star rating and comment.
          </p>
        )}
      </div>
    </section>
  );
}

export default ProductReviews;
